<script>
import axios from '@/utils/axios';
import HrmLeaveAllocateModal from '@/components/modal/hrm-leave-allocate-modal.vue';
import HrmLeaveMarkModal from '@/components/modal/hrm-leave-mark-modal.vue';

const SESSION_LABELS = {
  full_day: 'Full Day',
  half_day_morning: 'Half Day (AM)',
  half_day_afternoon: 'Half Day (PM)',
  short_day_morning: 'Short Day (AM)',
  short_day_afternoon: 'Short Day (PM)',
};

const MONTHS = [
  { v: 1, l: 'January' }, { v: 2, l: 'February' }, { v: 3, l: 'March' },
  { v: 4, l: 'April' }, { v: 5, l: 'May' }, { v: 6, l: 'June' },
  { v: 7, l: 'July' }, { v: 8, l: 'August' }, { v: 9, l: 'September' },
  { v: 10, l: 'October' }, { v: 11, l: 'November' }, { v: 12, l: 'December' },
];

export default {
  components: { HrmLeaveAllocateModal, HrmLeaveMarkModal },
  inject: ['setLoading'],
  data() {
    return {
      activeTab: 'requests',
      loading: true,
      requests: [],
      leaveTypes: [],
      leaveTypeMap: {},
      months: MONTHS,
      years: [2025, 2026, 2027],
      statusOptions: [
        { label: 'Pending', value: 'pending' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' },
        { label: 'Cancelled', value: 'cancelled' },
      ],
      filters: { status: '', month: '', year: '' },
      tableColumns: [
        { key: 'employee', label: 'Employee' },
        { key: 'leave_type', label: 'Leave Type' },
        { key: 'dates', label: 'Dates' },
        { key: 'total_days', label: 'Days' },
        { key: 'session', label: 'Session' },
        { key: 'status', label: 'Status' },
        { key: 'reason', label: 'Reason' },
        { key: 'actions', label: 'Actions', width: '150px' },
      ],
      pagination: { current_page: 1, per_page: 50, last_page: 1, total: 0, links: [] },
      approveItem: null,
      rejectItem: null, rejectReason: '', rejectError: '',
      cancelItem: null, cancelReason: '', cancelError: '',
      typeItem: null, newTypeId: '', typeError: '',

      // Balances tab
      balances: [],
      balLoading: false,
      balLoaded: false,
      employees: [],
      balFilters: { employee_id: '', leave_type_id: '', year: '' },
      balColumns: [
        { key: 'employee', label: 'Employee' },
        { key: 'leave_type', label: 'Leave Type' },
        { key: 'allocated', label: 'Allocated' },
        { key: 'used', label: 'Used' },
        { key: 'remaining', label: 'Remaining' },
        { key: 'usage', label: 'Usage', width: '190px' },
      ],
    };
  },
  computed: {
    balPagination() {
      return { current_page: 1, per_page: this.balances.length || 1, last_page: 1, total: this.balances.length, links: [] };
    },
    employeeOptions() {
      return this.employees.map(e => ({ id: e.id, label: this.empLabel(e) }));
    },
  },
  mounted() {
    this.fetchLeaveTypes();
    this.fetchRequests();
  },
  methods: {
    openAllocate(mode) { this.$refs.allocateModal.open(mode); },
    openMark() { this.$refs.markModal.open(); },

    switchTab(tab) {
      this.activeTab = tab;
      if (tab === 'balances' && !this.balLoaded) {
        this.fetchEmployees();
        this.fetchBalances();
      }
    },

    async fetchLeaveTypes() {
      try {
        const res = await axios.get('/hrms/leave-types/dropdown');
        this.leaveTypes = res.data.data || [];
        this.leaveTypeMap = {};
        this.leaveTypes.forEach(t => { this.leaveTypeMap[t.id] = t.code; });
      } catch (e) { console.log(e.message); }
    },
    async fetchRequests(page = 1) {
      this.loading = true;
      try {
        const res = await axios.get('/hrms/leaves/requests', {
          params: {
            page,
            status: this.filters.status || undefined,
            month: this.filters.month || undefined,
            year: this.filters.year || undefined,
          },
        });
        const d = res.data.data;
        this.requests = d.data;
        this.pagination = { current_page: d.current_page, per_page: d.per_page, last_page: d.last_page, total: d.total, links: d.links };
      } catch (e) { this.$swalToast(e.message, 'error'); }
      finally { this.loading = false; }
    },
    empName(row) {
      const e = row.employee || {};
      return e.full_name || [e.first_name, e.last_name].filter(Boolean).join(' ') || '-';
    },
    typeCode(row) { return this.leaveTypeMap[row.leave_type_id] || '-'; },
    sessionLabel(s) { return SESSION_LABELS[s] || s; },

    // Approve
    confirmApprove(row) { this.approveItem = row; this.$openModal('hrm_adm_approve_modal'); },
    async approve() {
      this.setLoading(true);
      try {
        const res = await axios.patch(`/hrms/leaves/${this.approveItem.id}/approve`);
        this.$swalToast(res.data.message || 'Leave approved.');
        this.$closeModal('hrm_adm_approve_modal');
        this.fetchRequests(this.pagination.current_page);
      } catch (e) { this.$swalToast(e.message, 'error'); }
      finally { this.setLoading(false); }
    },

    // Reject
    openReject(row) { this.rejectItem = row; this.rejectReason = ''; this.rejectError = ''; this.$openModal('hrm_adm_reject_modal'); },
    async reject() {
      if (!this.rejectReason.trim()) { this.rejectError = 'Rejection reason is required'; return; }
      this.setLoading(true);
      try {
        const res = await axios.patch(`/hrms/leaves/${this.rejectItem.id}/reject`, { rejection_reason: this.rejectReason.trim() });
        this.$swalToast(res.data.message || 'Leave rejected.');
        this.$closeModal('hrm_adm_reject_modal');
        this.fetchRequests(this.pagination.current_page);
      } catch (error) {
        if (error.type === 'validation') this.rejectError = error.errors?.rejection_reason?.[0] || 'Invalid reason';
        else this.$swalToast(error.message, 'error');
      } finally { this.setLoading(false); }
    },

    // Cancel
    openCancel(row) { this.cancelItem = row; this.cancelReason = ''; this.cancelError = ''; this.$openModal('hrm_adm_cancel_modal'); },
    async cancel() {
      if (!this.cancelReason.trim()) { this.cancelError = 'Cancellation reason is required'; return; }
      this.setLoading(true);
      try {
        const res = await axios.put(`/hrms/leaves/cancel-leave/${this.cancelItem.id}`, { cancellation_reason: this.cancelReason.trim() });
        this.$swalToast(res.data.message || 'Leave cancelled.');
        this.$closeModal('hrm_adm_cancel_modal');
        this.fetchRequests(this.pagination.current_page);
      } catch (error) {
        if (error.type === 'validation') this.cancelError = error.errors?.cancellation_reason?.[0] || 'Invalid reason';
        else this.$swalToast(error.message, 'error');
      } finally { this.setLoading(false); }
    },

    // Change Type
    openChangeType(row) { this.typeItem = row; this.newTypeId = row.leave_type_id || ''; this.typeError = ''; this.$openModal('hrm_adm_changetype_modal'); },
    async changeType() {
      if (!this.newTypeId) { this.typeError = 'Select a leave type'; return; }
      this.setLoading(true);
      try {
        const res = await axios.put(`/hrms/leaves/change-leave-type/${this.typeItem.id}`, { leave_type_id: this.newTypeId });
        this.$swalToast(res.data.message || 'Leave type changed.');
        this.$closeModal('hrm_adm_changetype_modal');
        this.fetchRequests(this.pagination.current_page);
      } catch (error) {
        if (error.type === 'validation') this.typeError = error.errors?.leave_type_id?.[0] || 'Invalid type';
        else this.$swalToast(error.message, 'error');
      } finally { this.setLoading(false); }
    },

    // ===== Balances tab =====
    async fetchEmployees() {
      try { const res = await axios.get('/hrms/employees/dropdown'); this.employees = res.data.data || []; }
      catch (e) { console.log(e.message); }
    },
    async fetchBalances() {
      this.balLoading = true;
      try {
        const res = await axios.get('/hrms/leaves/all-balance', {
          params: {
            employee_id: this.balFilters.employee_id || undefined,
            leave_type_id: this.balFilters.leave_type_id || undefined,
            year: this.balFilters.year || undefined,
          },
        });
        this.balances = res.data.data || [];
        this.balLoaded = true;
      } catch (e) { this.$swalToast(e.message, 'error'); }
      finally { this.balLoading = false; }
    },
    balEmpName(b) {
      const e = b.employee || {};
      return e.full_name || [e.first_name, e.last_name].filter(Boolean).join(' ') || '-';
    },
    empLabel(e) { return e.full_name || e.name || [e.first_name, e.last_name].filter(Boolean).join(' ') || `#${e.id}`; },
    num(v) { return Number(v) || 0; },
    remaining(b) { return (this.num(b.allocated) + this.num(b.carried_forward) - this.num(b.used)).toFixed(1).replace(/\.0$/, ''); },
    usagePct(b) {
      const tot = this.num(b.allocated) + this.num(b.carried_forward);
      if (tot <= 0) return 0;
      return Math.min(Math.round((this.num(b.used) / tot) * 100), 100);
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <h4 class="page-title mb-3">Leave Management</h4>
        <div class="d-flex gap-2 lv-header-actions">
          <a href="javascript:void(0);" class="btn btn-outline-primary d-flex align-items-center" @click="openAllocate('bulk')">
            <i class="ti ti-users me-2"></i>Bulk Allocate
          </a>
          <a href="javascript:void(0);" class="btn btn-outline-primary d-flex align-items-center" @click="openAllocate('single')">
            <i class="ti ti-wallet me-2"></i>Allocate
          </a>
          <a href="javascript:void(0);" class="btn btn-primary d-flex align-items-center" @click="openMark">
            <i class="ti ti-calendar-bolt me-2"></i>Mark Leave
          </a>
        </div>
      </div>

      <!-- Tabs -->
      <ul class="lv-tabs mb-3">
        <li :class="{ active: activeTab === 'requests' }" @click="switchTab('requests')">All Requests</li>
        <li :class="{ active: activeTab === 'balances' }" @click="switchTab('balances')">Balances</li>
      </ul>

      <!-- Requests -->
      <div v-show="activeTab === 'requests'" class="card border-0 shadow-sm mb-0" style="border-radius:14px;">
        <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2 py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            All Requests <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ pagination.total }})</span>
          </h5>
          <div class="d-flex gap-2 flex-wrap lv-filters">
            <vue-select class="lv-vsel" v-model="filters.status" :options="statusOptions" label="label" :reduce="s => s.value"
              placeholder="All Status" :clearable="true" @update:modelValue="fetchRequests(1)" style="min-width:150px;" />
            <vue-select class="lv-vsel" v-model="filters.month" :options="months" label="l" :reduce="m => m.v"
              placeholder="All Months" :clearable="true" @update:modelValue="fetchRequests(1)" style="min-width:150px;" />
            <vue-select class="lv-vsel" v-model="filters.year" :options="years"
              placeholder="All Years" :clearable="true" @update:modelValue="fetchRequests(1)" style="min-width:120px;" />
          </div>
        </div>

        <div class="card-body px-3 pt-2 pb-3" style="background:#f1f5f9;">
          <div class="lv-table-scroll">
          <app-table
            :columns="tableColumns" :rows="requests" :pagination="pagination" :loading="loading"
            empty-text="No leave requests." empty-icon="ti ti-calendar-off"
            @page-change="fetchRequests"
          >
            <template #employee="{ row }">
              <span class="fw-semibold" style="font-size:12.5px; color:#1e293b;">{{ empName(row) }}</span>
            </template>
            <template #leave_type="{ row }">
              <span class="lv-code">{{ typeCode(row) }}</span>
            </template>
            <template #dates="{ row }">
              <span v-if="row.from_date === row.to_date">{{ $formatDate(row.from_date) }}</span>
              <span v-else>{{ $formatDate(row.from_date) }} – {{ $formatDate(row.to_date) }}</span>
            </template>
            <template #total_days="{ row }">{{ row.total_days }}</template>
            <template #session="{ row }">
              <span style="font-size:11.5px; color:#64748b;">{{ sessionLabel(row.session) }}</span>
            </template>
            <template #status="{ row }">
              <span class="lv-st" :class="'lv-' + row.status">{{ row.status }}</span>
            </template>
            <template #reason="{ row }">
              <span style="font-size:12px; color:#64748b;">{{ row.reason || '-' }}</span>
            </template>
            <template #actions="{ row }">
              <div class="d-flex gap-1 justify-content-end">
                <button v-if="row.status === 'pending'" class="btn btn-sm btn-light text-success" title="Approve" @click="confirmApprove(row)"><i class="ti ti-check"></i></button>
                <button v-if="row.status === 'pending'" class="btn btn-sm btn-light text-danger" title="Reject" @click="openReject(row)"><i class="ti ti-x"></i></button>
                <button v-if="['pending','approved'].includes(row.status)" class="btn btn-sm btn-light" style="color:#4f46e5;" title="Change Type" @click="openChangeType(row)"><i class="ti ti-switch-horizontal"></i></button>
                <button v-if="['pending','approved'].includes(row.status)" class="btn btn-sm btn-light text-secondary" title="Cancel" @click="openCancel(row)"><i class="ti ti-ban"></i></button>
                <span v-if="['rejected','cancelled'].includes(row.status)" class="text-muted">-</span>
              </div>
            </template>
          </app-table>
          </div>
        </div>
      </div>

      <!-- Balances -->
      <div v-show="activeTab === 'balances'" class="card border-0 shadow-sm mb-0" style="border-radius:14px;">
        <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2 py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            Leave Balances <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ balances.length }})</span>
          </h5>
          <div class="d-flex gap-2 flex-wrap lv-filters">
            <vue-select class="lv-vsel" v-model="balFilters.employee_id" :options="employeeOptions" label="label" :reduce="e => e.id"
              placeholder="All Employees" :clearable="true" @update:modelValue="fetchBalances()" style="min-width:180px;" />
            <vue-select class="lv-vsel" v-model="balFilters.leave_type_id" :options="leaveTypes" label="code" :reduce="t => t.id"
              placeholder="All Types" :clearable="true" @update:modelValue="fetchBalances()" style="min-width:140px;" />
            <vue-select class="lv-vsel" v-model="balFilters.year" :options="years"
              placeholder="All Years" :clearable="true" @update:modelValue="fetchBalances()" style="min-width:120px;" />
          </div>
        </div>

        <div class="card-body px-3 pt-2 pb-3" style="background:#f1f5f9;">
          <div class="lv-table-scroll">
          <app-table
            :columns="balColumns" :rows="balances" :pagination="balPagination" :loading="balLoading"
            empty-text="No balances allocated." empty-icon="ti ti-wallet-off"
            @page-change="() => {}"
          >
            <template #employee="{ row }">
              <span class="fw-semibold" style="font-size:12.5px; color:#1e293b;">{{ balEmpName(row) }}</span>
            </template>
            <template #leave_type="{ row }">
              <span class="lv-code">{{ row.leave_type?.code || '-' }}</span>
              <span style="font-size:11.5px; color:#94a3b8; margin-left:6px;">{{ row.leave_type?.name }}</span>
            </template>
            <template #allocated="{ row }">{{ row.allocated }}</template>
            <template #used="{ row }">{{ row.used }}</template>
            <template #remaining="{ row }">
              <span class="fw-bold" style="color:#16a34a;">{{ remaining(row) }}</span>
            </template>
            <template #usage="{ row }">
              <div class="lv-bar"><div class="lv-bar-fill" :style="{ width: usagePct(row) + '%' }"></div></div>
              <span class="lv-bar-pct">{{ usagePct(row) }}% used</span>
            </template>
          </app-table>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <!-- Approve -->
  <div class="modal fade" id="hrm_adm_approve_modal">
    <div class="modal-dialog modal-dialog-centered"><div class="modal-content"><div class="modal-body text-center">
      <span class="avatar avatar-xl bg-transparent-success text-success mb-3"><i class="ti ti-calendar-check fs-36"></i></span>
      <h4 class="mb-1">Approve Leave</h4>
      <p class="mb-3">Approve this request? Balance will be deducted.</p>
      <div class="d-flex justify-content-center">
        <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">No</a>
        <a href="javascript:void(0);" class="btn btn-success" @click="approve">Yes, Approve</a>
      </div>
    </div></div></div>
  </div>

  <!-- Reject -->
  <div class="modal fade" id="hrm_adm_reject_modal">
    <div class="modal-dialog modal-dialog-centered"><div class="modal-content">
      <div class="modal-header"><h5 class="modal-title">Reject Leave</h5><button type="button" class="btn-close" data-bs-dismiss="modal"></button></div>
      <div class="modal-body">
        <label class="lv-lbl">Rejection Reason <span class="text-danger">*</span></label>
        <textarea class="form-control" rows="3" maxlength="500" v-model="rejectReason" :class="{ 'is-invalid': rejectError }" placeholder="Reason for rejection"></textarea>
        <div v-if="rejectError" class="lv-err">{{ rejectError }}</div>
      </div>
      <div class="modal-footer">
        <a href="javascript:void(0);" class="btn btn-light" data-bs-dismiss="modal">Cancel</a>
        <a href="javascript:void(0);" class="btn btn-danger" @click="reject">Reject</a>
      </div>
    </div></div>
  </div>

  <!-- Cancel -->
  <div class="modal fade" id="hrm_adm_cancel_modal">
    <div class="modal-dialog modal-dialog-centered"><div class="modal-content">
      <div class="modal-header"><h5 class="modal-title">Cancel Leave</h5><button type="button" class="btn-close" data-bs-dismiss="modal"></button></div>
      <div class="modal-body">
        <label class="lv-lbl">Cancellation Reason <span class="text-danger">*</span></label>
        <textarea class="form-control" rows="3" maxlength="500" v-model="cancelReason" :class="{ 'is-invalid': cancelError }" placeholder="Reason for cancellation"></textarea>
        <div v-if="cancelError" class="lv-err">{{ cancelError }}</div>
      </div>
      <div class="modal-footer">
        <a href="javascript:void(0);" class="btn btn-light" data-bs-dismiss="modal">Back</a>
        <a href="javascript:void(0);" class="btn btn-danger" @click="cancel">Cancel Leave</a>
      </div>
    </div></div>
  </div>

  <!-- Change Type -->
  <div class="modal fade" id="hrm_adm_changetype_modal">
    <div class="modal-dialog modal-dialog-centered"><div class="modal-content">
      <div class="modal-header"><h5 class="modal-title">Change Leave Type</h5><button type="button" class="btn-close" data-bs-dismiss="modal"></button></div>
      <div class="modal-body">
        <label class="lv-lbl">New Leave Type <span class="text-danger">*</span></label>
        <select class="form-select" v-model="newTypeId" :class="{ 'is-invalid': typeError }">
          <option value="">Select leave type</option>
          <option v-for="t in leaveTypes" :key="t.id" :value="t.id">{{ t.code }}</option>
        </select>
        <div v-if="typeError" class="lv-err">{{ typeError }}</div>
      </div>
      <div class="modal-footer">
        <a href="javascript:void(0);" class="btn btn-light" data-bs-dismiss="modal">Back</a>
        <a href="javascript:void(0);" class="btn btn-primary" @click="changeType">Save</a>
      </div>
    </div></div>
  </div>

  <hrm-leave-allocate-modal ref="allocateModal" @updated="activeTab === 'balances' && fetchBalances()" />
  <hrm-leave-mark-modal ref="markModal" @updated="fetchRequests(pagination.current_page)" />
</template>

<style scoped>
.lv-code { display: inline-block; font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 7px; background: #eef2ff; color: #4f46e5; }
.lv-st { display: inline-block; font-size: 10px; font-weight: 600; padding: 3px 10px; border-radius: 20px; text-transform: capitalize; }
.lv-pending { background: #fef3c7; color: #b45309; }
.lv-approved { background: #dcfce7; color: #16a34a; }
.lv-rejected { background: #fee2e2; color: #dc2626; }
.lv-cancelled { background: #f1f5f9; color: #94a3b8; }
.lv-lbl { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 5px; display: block; }
.lv-err { font-size: 11px; color: #dc2626; margin-top: 4px; }
.form-control, .form-select { border-radius: 10px; border-color: #e2e8f0; font-size: 13.5px; }
.form-control:focus, .form-select:focus { border-color: #a5b4fc; box-shadow: 0 0 0 3px rgba(79,70,229,.1); }
.btn-primary { background: linear-gradient(135deg, #4f46e5, #7c3aed); border: none; }

/* Tabs */
.lv-tabs { display:flex; gap:6px; list-style:none; padding:4px; margin:0; background:#fff; border-radius:12px; width:fit-content; box-shadow:0 1px 2px rgba(15,23,42,.04); }
.lv-tabs li { font-size:13px; font-weight:600; color:#64748b; padding:8px 18px; border-radius:9px; cursor:pointer; transition:all .15s; }
.lv-tabs li:hover { color:#4f46e5; }
.lv-tabs li.active { background:linear-gradient(135deg,#4f46e5,#7c3aed); color:#fff; }

/* Usage bar */
.lv-bar { height:7px; background:#e2e8f0; border-radius:6px; overflow:hidden; }
.lv-bar-fill { height:100%; background:linear-gradient(90deg,#4f46e5,#7c3aed); border-radius:6px; transition:width .5s ease; }
.lv-bar-pct { font-size:10.5px; color:#94a3b8; }

/* vue-select filters */
:deep(.lv-vsel .vs__dropdown-toggle) { border:1px solid #e2e8f0; border-radius:10px; min-height:34px; background:#fff; padding:0 4px; }
:deep(.lv-vsel .vs__dropdown-toggle:focus-within) { border-color:#a5b4fc; box-shadow:0 0 0 3px rgba(79,70,229,.1); }
:deep(.lv-vsel .vs__search) { font-size:12.5px; margin:0; padding:2px 4px; }
:deep(.lv-vsel .vs__selected) { font-size:12.5px; color:#1e293b; margin:2px; }
:deep(.lv-vsel .vs__dropdown-option) { font-size:12.5px; }
:deep(.lv-vsel .vs__dropdown-menu) { font-size:12.5px; }
:deep(.lv-vsel .vs__clear), :deep(.lv-vsel .vs__open-indicator) { fill:#94a3b8; }

/* Table horizontal scroll on small screens */
.lv-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.lv-table-scroll :deep(table) { min-width: 720px; }

/* ===== Responsive fixes ===== */
@media (max-width: 767px) {
  .page-breadcrumb { flex-direction: column; align-items: flex-start !important; }
  .lv-header-actions { width: 100%; flex-wrap: wrap; }
  .lv-header-actions .btn { flex: 1 1 calc(50% - 8px); justify-content: center; font-size: 12px; padding: 8px 10px; white-space: nowrap; }

  .lv-tabs { width: 100%; }
  .lv-tabs li { flex: 1; text-align: center; padding: 8px 10px; font-size: 12px; }

  .card-header { flex-direction: column; align-items: flex-start !important; }
  .lv-filters { width: 100%; }
  .lv-filters .lv-vsel { flex: 1 1 100%; min-width: 100% !important; }
}

@media (min-width: 768px) and (max-width: 991px) {
  .lv-filters .lv-vsel { min-width: 130px !important; flex: 1; }
}
</style>